import { Table as MantineTable, TableProps, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef, useEffect, useRef } from 'react';
import { useMergedRef } from '@mantine/hooks';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type TableVariant = 'default' | 'striped' | 'hoverable' | 'bordered' | 'minimal';

export interface ThemedTableProps extends Omit<TableProps, 'striped' | 'highlightOnHover' | 'withTableBorder' | 'withColumnBorders'> {
  tableVariant?: TableVariant;
  /**
   * On mobile, stack each row into a labelled "item card" instead of a
   * horizontally-scrolling table. Column headers are auto-applied as the labels.
   * Desktop/tablet are unaffected. Default: true.
   */
  responsiveCards?: boolean;
}

interface TableComponent extends React.ForwardRefExoticComponent<ThemedTableProps & React.RefAttributes<HTMLTableElement>> {
  Thead: typeof MantineTable.Thead;
  Tbody: typeof MantineTable.Tbody;
  Tfoot: typeof MantineTable.Tfoot;
  Tr: typeof MantineTable.Tr;
  Th: typeof MantineTable.Th;
  Td: typeof MantineTable.Td;
  Caption: typeof MantineTable.Caption;
}

const Table = forwardRef<HTMLTableElement, ThemedTableProps>(
  ({ tableVariant = 'default', responsiveCards = true, children, style, className, ...rest }, ref) => {
    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    // Auto-label body cells with their column header so the mobile card view
    // (CSS in index.css, .ui-responsive-table) can show "Header: value" rows.
    const localRef = useRef<HTMLTableElement>(null);
    const mergedRef = useMergedRef(ref, localRef);
    useEffect(() => {
      if (!responsiveCards) return;
      const table = localRef.current;
      if (!table) return;
      const applyLabels = () => {
        const headRow = table.tHead?.rows?.[0];
        if (!headRow) return;
        const headers = Array.from(headRow.cells).map((c) => (c as HTMLElement).innerText.trim());
        Array.from(table.tBodies).forEach((tbody) => {
          Array.from(tbody.rows).forEach((row) => {
            if (row.cells.length !== headers.length) return; // skip colspan/empty-state rows
            Array.from(row.cells).forEach((td, i) => {
              if (headers[i]) td.setAttribute('data-label', headers[i]);
            });
          });
        });
      };
      applyLabels();
      const observer = new MutationObserver(applyLabels);
      observer.observe(table, { childList: true, subtree: true });
      return () => observer.disconnect();
    }, [responsiveCards, children]);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const variantProps: Record<TableVariant, Partial<TableProps>> = {
      default: {
        striped: false,
        highlightOnHover: false,
        withTableBorder: false,
        withColumnBorders: false,
      },
      striped: {
        striped: true,
        stripedColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0],
        highlightOnHover: false,
        withTableBorder: false,
        withColumnBorders: false,
      },
      hoverable: {
        striped: false,
        highlightOnHover: true,
        highlightOnHoverColor: isDark ? theme.colors.dark[5] : theme.colors.gray[1],
        withTableBorder: false,
        withColumnBorders: false,
      },
      bordered: {
        striped: false,
        highlightOnHover: false,
        withTableBorder: true,
        withColumnBorders: true,
      },
      minimal: {
        striped: false,
        highlightOnHover: false,
        withTableBorder: false,
        withColumnBorders: false,
        verticalSpacing: 'xs',
        horizontalSpacing: 'xs',
      },
    };

    return (
      <MantineTable
        ref={mergedRef}
        className={[responsiveCards ? 'ui-responsive-table' : '', className].filter(Boolean).join(' ') || undefined}
        style={{ ...accessibilityStyles, ...style }}
        {...variantProps[tableVariant]}
        {...rest}
      >
        {children}
      </MantineTable>
    );
  }
) as TableComponent;

Table.displayName = 'Table';

// Attach subcomponents
Table.Thead = MantineTable.Thead;
Table.Tbody = MantineTable.Tbody;
Table.Tfoot = MantineTable.Tfoot;
Table.Tr = MantineTable.Tr;
Table.Th = MantineTable.Th;
Table.Td = MantineTable.Td;
Table.Caption = MantineTable.Caption;

export default Table;
